import { createSlice } from '@reduxjs/toolkit';

import { fetchCensusReports, fetchHospitalUsers, fetchUsersForAssignment } from './census-api';
import type { CensusReportListItem, HospitalUser } from '@/types/census';
import { DEFAULT_ROWS_PER_PAGE } from '@/constant';
import { logout } from '../auth/logout';
export interface CensusState {
    censusReports: CensusReportListItem[];
    censusReportsTotal: number;
    censusReportsPage: number;
    censusReportsLimit: number;
    censusReportsLoading: boolean;
    censusReportsError: string | null;
    hospitalUsers: HospitalUser[];
    hospitalUsersLoading: boolean;
    hospitalUsersError: string | null;
    currentCensusReportsRequestId: string | null;
}

const initialState: CensusState = {
    censusReports: [],
    censusReportsTotal: 0,
    censusReportsPage: 1,
    censusReportsLimit: DEFAULT_ROWS_PER_PAGE,
    censusReportsLoading: false,
    censusReportsError: null,
    hospitalUsers: [],
    hospitalUsersLoading: false,
    hospitalUsersError: null,
    currentCensusReportsRequestId: null
};

const censusSlice = createSlice({
    name: 'census',
    initialState,
    reducers: {
        clearCensusState: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCensusReports.pending, (state, action) => {
                state.censusReportsLoading = true;
                state.censusReportsError = null;
                state.currentCensusReportsRequestId = action.meta.requestId;
            })
            .addCase(fetchCensusReports.fulfilled, (state, { meta, payload }) => {
                if (state.currentCensusReportsRequestId !== meta.requestId) {
                    return;
                }
                state.censusReportsLoading = false;
                state.censusReports = payload.items;
                state.censusReportsTotal = payload.total;
                state.censusReportsPage = payload.page;
                state.censusReportsLimit = payload.limit;
                state.censusReportsError = null;
                state.currentCensusReportsRequestId = null;
            })
            .addCase(fetchCensusReports.rejected, (state, { meta, payload, error }) => {
                if (state.currentCensusReportsRequestId !== meta.requestId) {
                    return;
                }
                state.censusReportsLoading = false;
                if (error.name === 'AbortError') {
                    state.censusReportsError = null;
                } else {
                    state.censusReportsError = payload ?? 'Failed to fetch census reports';
                }
                state.currentCensusReportsRequestId = null;
            })
            .addCase(fetchHospitalUsers.pending, (state) => {
                state.hospitalUsersLoading = true;
                state.hospitalUsersError = null;
            })
            .addCase(fetchHospitalUsers.fulfilled, (state, { payload }) => {
                state.hospitalUsersLoading = false;
                state.hospitalUsers = payload;
                state.hospitalUsersError = null;
            })
            .addCase(fetchHospitalUsers.rejected, (state, { payload }) => {
                state.hospitalUsersLoading = false;
                state.hospitalUsersError = payload ?? 'Failed to fetch hospital users';
            })
            .addCase(fetchUsersForAssignment.pending, (state) => {
                state.hospitalUsersLoading = true;
                state.hospitalUsersError = null;
            })
            .addCase(fetchUsersForAssignment.fulfilled, (state, { payload }) => {
                state.hospitalUsersLoading = false;
                state.hospitalUsers = payload;
                state.hospitalUsersError = null;
            })
            .addCase(fetchUsersForAssignment.rejected, (state, { payload, error }) => {
                state.hospitalUsersLoading = false;
                if (error.name === 'AbortError') {
                    state.hospitalUsersError = null;
                    return;
                }
                state.hospitalUsersError = payload ?? 'Failed to fetch users';
            })
            .addCase(logout, () => initialState);
    }
});

export const { clearCensusState } = censusSlice.actions;
export default censusSlice.reducer;
